"use client";

import type * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { BookingWizard } from "@/components/booking/BookingWizard";
import { BookingPriceDetailsModal } from "@/components/booking/BookingPriceDetailsModal";
import {
  buildBookingRentalDataParamsKey,
  parseBookingRentalDataParamsKey,
} from "@/lib/booking/rental-data";
import {
  buildInclusionItems,
  getIncludedOverviewLabels,
  parseBookingMiscellaneousParams,
} from "@/lib/booking/package-inclusions";
import { appendSearchToPath } from "@/lib/booking/search-params";
import { useAppDispatch } from "@/lib/redux/hooks";
import { setSelectedMiscellaneousDetails } from "@/lib/redux/features/user-selected-details/userSelectedDetailsSlice";
import { bookingRentalService } from "@/lib/services/booking-rental-service";
import { miscellaneousService } from "@/lib/services/miscellaneous-service";
import type { RentalChargesBreakdown } from "@/lib/types/booking-rental";
import type {
  BookingExtraMiscellaneousQueryParams,
  MiscellaneousMasterDataItem,
  ProtectionPackage,
} from "@/lib/types/miscellaneous";
import { formatAud } from "@/lib/vehicles";
import {
  LucideBaby,
  LucideCheck,
  LucideFuel,
  LucideGauge,
  LucideInfo,
  LucideLoaderCircle,
  LucideMinus,
  LucidePlus,
  LucideShield,
  LucideShoppingCart,
  LucideSnowflake,
  LucideUserPlus,
  LucideWrench,
  LucideBaggageClaim,
} from "lucide-react";
import { Switch } from "../ui/switch";
import { cn } from "@/lib/utils";
import { Button } from "../ui/button";

const ADD_ON_DETAILS: Record<string, string> = {
  additional_driver:
    "Add an additional authorised driver to your rental agreement for the duration of the booking.",
  ADDITIONAL_DRIVER:
    "Add an additional authorised driver to your rental agreement for the duration of the booking.",
  baby_seat_rear:
    "These car seats are for babies who weigh up to 9 kilograms.",
  baby_seat_forward:
    "Forward-facing child seat suitable for children weighing 9–18 kg.",
  child_booster_seat:
    "Booster seat for children weighing 18 kg and above.",
  CHILD_SEAT:
    "Child seat suitable for your rental journey.",
  roadside_protection:
    "24/7 roadside assistance for breakdowns, flat tyres, lockouts, and other emergencies.",
  loss_damage_waiver:
    "Reduces your financial liability for damage to or theft of the rental vehicle.",
  tyre_windscreen_protection:
    "Covers repair or replacement of tyres and windscreen damage during your rental.",
  interior_protection:
    "Protection against interior stains, odours, and damage caused during your rental.",
  snow_pass:
    "Snow chains and pass required for driving in designated snow-affected areas.",
  SNOW_COVER:
    "Snow cover for driving in designated snow-affected areas.",
  refuelling_service:
    "Return the vehicle without refuelling — we will handle refuelling for you.",
  HAND_TROLLY: "Hand trolley available for loading and unloading during your rental.",
  EXTRA_100_KM: "Add an extra 100 km allowance per day to your rental.",
};

type AddonIconProps = {
  size?: number;
  className?: string;
};

const ADD_ON_ICONS: Record<
  string,
  React.ComponentType<AddonIconProps>
> = {
  additional_driver: LucideUserPlus,
  ADDITIONAL_DRIVER: LucideUserPlus,
  baby_seat_rear: LucideBaby,
  baby_seat_forward: LucideBaby,
  child_booster_seat: LucideBaby,
  CHILD_SEAT: LucideBaby,
  roadside_protection: LucideWrench,
  loss_damage_waiver: LucideShield,
  tyre_windscreen_protection: LucideShield,
  interior_protection: LucideShield,
  snow_pass: LucideSnowflake,
  SNOW_COVER: LucideSnowflake,
  refuelling_service: LucideFuel,
  HAND_TROLLY: LucideBaggageClaim,
  EXTRA_100_KM: LucideGauge,
};

export const BookingStepThree: React.FC = () => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const dispatch = useAppDispatch();
  const [addOns, setAddOns] = useState<MiscellaneousMasterDataItem[]>([]);
  const [packages, setPackages] = useState<ProtectionPackage[]>([]);
  const [miscellaneousMasterData, setMiscellaneousMasterData] = useState<
    MiscellaneousMasterDataItem[]
  >([]);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [expandedKeys, setExpandedKeys] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [priceBreakdown, setPriceBreakdown] = useState<RentalChargesBreakdown | null>(
    null,
  );
  const [bookingTotal, setBookingTotal] = useState<number | null>(null);
  const [isRentalDataLoading, setIsRentalDataLoading] = useState(false);
  const [isPriceDetailsLoading, setIsPriceDetailsLoading] = useState(false);
  const [priceDetailsOpen, setPriceDetailsOpen] = useState(false);
  const [priceDetailsError, setPriceDetailsError] = useState<string | null>(null);

  const bookingMiscellaneousParams = useMemo(
    () => parseBookingMiscellaneousParams(searchParams),
    [searchParams],
  );

  const selectedPackageKey = searchParams.get("selectedPackage") ?? "";

  const requestParams = useMemo<BookingExtraMiscellaneousQueryParams | null>(() => {
    const selectedPackage = searchParams.get("selectedPackage");

    if (!selectedPackage) {
      return null;
    }

    return {
      selected_package: selectedPackage,
    };
  }, [searchParams]);

  const rentalDays = useMemo(() => {
    const fromDate = searchParams.get("pickupDate");
    const toDate = searchParams.get("returnDate");
    if (!fromDate || !toDate) {
      return 1;
    }
    return getRentalDays(fromDate, toDate);
  }, [searchParams]);

  useEffect(() => {
    let isMounted = true;

    const loadAddOns = async () => {
      if (!requestParams) {
        setError("Missing required URL param for loading add-ons (selectedPackage).");
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const response =
          await miscellaneousService.fetchBookingExtraMiscellaneous(
            requestParams,
          );

        if (!response.success) {
          throw new Error(response.message || "Unable to load add-ons.");
        }

        if (!isMounted) {
          return;
        }

        setAddOns(response.data ?? []);
      } catch (loadError) {
        if (!isMounted) {
          return;
        }
        const message =
          loadError instanceof Error
            ? loadError.message
            : "Unable to load add-ons.";
        setError(message);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void loadAddOns();

    return () => {
      isMounted = false;
    };
  }, [requestParams]);

  useEffect(() => {
    let isMounted = true;

    const loadPackageInclusions = async () => {
      if (!bookingMiscellaneousParams) {
        return;
      }

      try {
        const response = await miscellaneousService.fetchBookingMiscellaneous(
          bookingMiscellaneousParams,
        );

        if (!response.success || !isMounted) {
          return;
        }

        setPackages(response.data.packages ?? []);
        setMiscellaneousMasterData(response.data.miscellaneous_master_data ?? []);
      } catch {
        if (!isMounted) {
          return;
        }
      }
    };

    void loadPackageInclusions();

    return () => {
      isMounted = false;
    };
  }, [bookingMiscellaneousParams]);

  const rentalDataParamsKey = useMemo(
    () => buildBookingRentalDataParamsKey(searchParams, addOns, quantities),
    [searchParams.toString(), addOns, quantities],
  );

  const rentalDataParams = useMemo(
    () => parseBookingRentalDataParamsKey(rentalDataParamsKey),
    [rentalDataParamsKey],
  );

  useEffect(() => {
    let isMounted = true;

    const loadRentalData = async () => {
      if (!rentalDataParamsKey || isLoading || error) {
        return;
      }

      const params = parseBookingRentalDataParamsKey(rentalDataParamsKey);
      if (!params) {
        return;
      }

      try {
        setIsRentalDataLoading(true);
        const response = await bookingRentalService.fetchBookingRentalData(
          params,
        );

        if (!response.success) {
          throw new Error(response.message || "Unable to load price details.");
        }

        if (!isMounted) {
          return;
        }

        setPriceBreakdown(response.data.rental_charges_breakdown);
        setBookingTotal(response.data.rental_charges_breakdown.total.amount);
      } catch {
        if (!isMounted) {
          return;
        }
      } finally {
        if (isMounted) {
          setIsRentalDataLoading(false);
        }
      }
    };

    void loadRentalData();

    return () => {
      isMounted = false;
    };
  }, [rentalDataParamsKey, isLoading, error]);

  const handlePriceDetailsClick = async () => {
    setPriceDetailsOpen(true);
    setPriceDetailsError(null);

    if (!rentalDataParams) {
      setPriceDetailsError(
        "Missing required booking details to load price breakdown.",
      );
      return;
    }

    try {
      setIsPriceDetailsLoading(true);
      const response = await bookingRentalService.fetchBookingRentalData(
        rentalDataParams,
      );

      if (!response.success) {
        throw new Error(response.message || "Unable to load price details.");
      }

      setPriceBreakdown(response.data.rental_charges_breakdown);
      setBookingTotal(response.data.rental_charges_breakdown.total.amount);
    } catch (loadError) {
      const message =
        loadError instanceof Error
          ? loadError.message
          : "Unable to load price details.";
      setPriceDetailsError(message);
    } finally {
      setIsPriceDetailsLoading(false);
    }
  };

  const selectedPackage = useMemo(
    () => packages.find((item) => item.key === selectedPackageKey) ?? null,
    [packages, selectedPackageKey],
  );

  const packageIncludedItems = useMemo(() => {
    const inclusionItems = buildInclusionItems(miscellaneousMasterData, packages);
    return getIncludedOverviewLabels(selectedPackage, inclusionItems);
  }, [miscellaneousMasterData, packages, selectedPackage]);

  const packageTotal = useMemo(() => {
    if (!selectedPackage?.price_per_day) {
      return 0;
    }
    return selectedPackage.price_per_day * rentalDays;
  }, [selectedPackage, rentalDays]);

  const overviewItems = useMemo(() => {
    const items: string[] = [...packageIncludedItems];

    addOns.forEach((item) => {
      const quantity = quantities[item.key] ?? 0;
      if (quantity <= 0) {
        return;
      }

      if (quantity === 1 && item.maximum_packs === 1) {
        items.push(item.name);
        return;
      }

      items.push(`${quantity} ${item.name}`);
    });

    return items;
  }, [addOns, quantities, packageIncludedItems]);

  const setQuantity = (key: string, value: number, maximum: number) => {
    setQuantities((current) => ({
      ...current,
      [key]: Math.max(0, Math.min(maximum, value)),
    }));
  };

  const toggleDetails = (key: string) => {
    setExpandedKeys((current) => ({
      ...current,
      [key]: !current[key],
    }));
  };

  const continueHref = useMemo(
    () => appendSearchToPath("/booking/confirm", searchParams),
    [searchParams],
  );

  const handleContinue = () => {
    dispatch(
      setSelectedMiscellaneousDetails({
        addOns,
        quantities,
      }),
    );
    router.push(continueHref);
  };

  const headerAction = error ? undefined : (
    <div className="flex items-center gap-6">
      {!isLoading && addOns.length > 0 ? (
        <div className="text-right">
          <p className="text-sm text-gray-600">
            {priceBreakdown?.total.name ?? "Total"}:{" "}
            {isRentalDataLoading || isPriceDetailsLoading ? (
              <span className="inline-block perspective-[400px]">
                <span
                  className="animate-spin-horizontal text-2xl font-bold text-gray-900"
                  aria-label="Loading total"
                >
                  $
                </span>
              </span>
            ) : (
              <span className="text-2xl font-bold text-gray-900">
                AU$ {formatAud(bookingTotal ?? 0)}
              </span>
            )}
          </p>
          <button
            type="button"
            onClick={() => void handlePriceDetailsClick()}
            className="cursor-pointer text-sm text-gray-500 underline-offset-2 hover:underline"
          >
            Price details
          </button>
        </div>
      ) : null}
      <Button
        size="xl"
        onClick={handleContinue}
        disabled={isLoading || addOns.length === 0}
      >
        Continue
      </Button>
    </div>
  );

  return (
    <BookingWizard step={3} headerAction={headerAction}>
      <div className="px-12 py-6">
        {isLoading ? (
          <div className="flex flex-col gap-4">
            <div className="flex justify-center">
              <LucideLoaderCircle className="animate-spin text-blue-300" size={48}/>
            </div>
            <p className="text-center text-lg font-medium text-gray-800">Loading add-ons...</p>
          </div>
        ) : error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 px-6 py-10 text-center text-sm text-red-700">
            {error}
          </div>
        ) : addOns.length === 0 ? (
          <div className="rounded-xl border border-gray-200 bg-white px-6 py-10 text-center text-sm text-gray-500">
            No add-ons available right now.
          </div>
        ) : (
          <div className="flex flex-col gap-6 xl:flex-row xl:items-start">
            <div className="flex flex-1 flex-col gap-3">
              {addOns.map((item) => (
                <AddonCard
                  key={item.key}
                  item={item}
                  quantity={quantities[item.key] ?? 0}
                  isDetailsOpen={Boolean(expandedKeys[item.key])}
                  onToggleDetails={() => toggleDetails(item.key)}
                  onQuantityChange={(value) =>
                    setQuantity(item.key, value, item.maximum_packs)
                  }
                />
              ))}
            </div>

            <aside className="flex w-full shrink-0 flex-col gap-4 xl:w-96">
              <div className="rounded-xl bg-slate-100 px-6 py-5">
                {selectedPackage ? (
                  <div className="border-b border-gray-200 pb-5">
                    <p className="text-sm font-medium text-gray-600">
                      Selected protection package
                    </p>
                    <h3 className="mt-1 text-lg font-semibold text-gray-900">
                      {selectedPackage.name}
                    </h3>
                    <div className="mt-2">
                      {selectedPackage.price_per_day === null ? (
                        <p className="text-xl font-bold text-gray-900">Included</p>
                      ) : (
                        <>
                          <div className="flex items-end gap-1.5">
                            <span className="text-2xl font-bold text-gray-900">
                              AU$ {formatAud(selectedPackage.price_per_day)}
                            </span>
                            <span className="pb-0.5 text-sm text-gray-600">/ day</span>
                            {selectedPackage.original_price_per_day !== null ? (
                              <span className="pb-0.5 text-xs font-medium text-gray-600 line-through">
                                ${formatAud(selectedPackage.original_price_per_day)}
                              </span>
                            ) : null}
                          </div>
                          {rentalDays > 1 ? (
                            <p className="mt-1 text-sm text-gray-600">
                              AU$ {formatAud(packageTotal)} for {rentalDays} days
                            </p>
                          ) : null}
                        </>
                      )}
                    </div>
                  </div>
                ) : null}

                <h3
                  className={cn(
                    "text-lg font-semibold text-gray-900",
                    selectedPackage ? "mt-5" : undefined,
                  )}
                >
                  Your booking overview:
                </h3>
                <ul className="mt-4 space-y-2">
                  {overviewItems.map((label) => (
                    <li
                      key={label}
                      className="flex items-start justify-between gap-2 text-sm text-gray-700"
                    >
                      <span className="flex items-start gap-2">
                        <LucideCheck
                          size={14}
                          className="mt-0.5 shrink-0 text-green-600"
                        />
                        {label}
                      </span>
                      <LucideInfo
                        size={14}
                        className="mt-0.5 shrink-0 text-gray-400"
                      />
                    </li>
                  ))}
                </ul>
                <p className="mt-5 text-xs text-gray-500">
                  Best price — Free cancellation and rebooking within 24h.
                </p>
              </div>
            </aside>
          </div>
        )}
      </div>

      <BookingPriceDetailsModal
        open={priceDetailsOpen}
        onClose={() => setPriceDetailsOpen(false)}
        breakdown={priceBreakdown}
        isLoading={isPriceDetailsLoading && !priceBreakdown}
        error={priceDetailsError}
      />
    </BookingWizard>
  );
};

type AddonCardProps = {
  item: MiscellaneousMasterDataItem;
  quantity: number;
  isDetailsOpen: boolean;
  onToggleDetails: () => void;
  onQuantityChange: (value: number) => void;
};

const AddonCard: React.FC<AddonCardProps> = ({
  item,
  quantity,
  isDetailsOpen,
  onToggleDetails,
  onQuantityChange,
}) => {
  const Icon = ADD_ON_ICONS[item.key] ?? LucideShield;
  const details =
    ADD_ON_DETAILS[item.key] ??
    `Add ${item.name.toLowerCase()} to your rental booking.`;
  const unitLabel = formatUnitLabel(item.unit);
  const isQuantityControl = item.maximum_packs > 1;

  return (
    <article className="rounded-xl border border-gray-300 bg-white p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 flex-1 gap-3">
          <Icon size={28} className="mt-0.5 shrink-0 text-gray-700" />
          <div className="min-w-0 flex-1">
            <h4 className="text-base font-semibold text-gray-900">
              {item.name}
            </h4>
            <p className="mt-1 text-sm font-medium text-gray-700">
              {item.price_per_day !== null ? (
                <>
                  ${formatAud(item.price_per_day)}
                  {unitLabel ? (
                    <span className="font-normal text-gray-600">
                      {" "}
                      {unitLabel}
                    </span>
                  ) : null}
                </>
              ) : (
                <span className="font-normal text-gray-600">Included</span>
              )}
            </p>
          </div>
        </div>

        {isQuantityControl ? (
          <QuantityStepper
            value={quantity}
            max={item.maximum_packs}
            onChange={onQuantityChange}
          />
        ) : (
          <Switch
            checked={quantity > 0}
            onCheckedChange={(checked) => onQuantityChange(checked ? 1 : 0)}
            aria-label={`Toggle ${item.name}`}
          />
        )}
      </div>

      <button
        type="button"
        onClick={onToggleDetails}
        className="mt-3 cursor-pointer text-sm font-medium text-gray-900 underline-offset-2 hover:underline"
      >
        {isDetailsOpen ? "Close details" : "Details"}
      </button>

      {isDetailsOpen ? (
        <div className="mt-3 rounded-lg bg-gray-100 px-4 py-3 text-sm text-gray-700">
          {details}
        </div>
      ) : null}
    </article>
  );
};

type QuantityStepperProps = {
  value: number;
  max: number;
  onChange: (value: number) => void;
};

const QuantityStepper: React.FC<QuantityStepperProps> = ({
  value,
  max,
  onChange,
}) => {
  return (
    <div className="inline-flex items-center overflow-hidden rounded-full bg-gray-900 text-white">
      <button
        type="button"
        onClick={() => onChange(value - 1)}
        disabled={value <= 0}
        aria-label="Decrease quantity"
        className={cn(
          "flex size-9 items-center justify-center transition-colors hover:bg-gray-800",
          value <= 0 && "cursor-not-allowed opacity-40",
        )}
      >
        <LucideMinus size={16} />
      </button>
      <span className="min-w-8 text-center text-sm font-semibold">
        {value}
      </span>
      <button
        type="button"
        onClick={() => onChange(value + 1)}
        disabled={value >= max}
        aria-label="Increase quantity"
        className={cn(
          "flex size-9 items-center justify-center transition-colors hover:bg-gray-800",
          value >= max && "cursor-not-allowed opacity-40",
        )}
      >
        <LucidePlus size={16} />
      </button>
    </div>
  );
};

function formatUnitLabel(units: string[]): string {
  const hasPerDay = units.includes("per_day");
  const hasPerDriver = units.includes("per_driver");

  if (hasPerDay && hasPerDriver) {
    return "/ day & driver";
  }

  if (hasPerDay) {
    return "/ day";
  }

  return "";
}

function getRentalDays(pickupDate: string, returnDate: string): number {
  const start = new Date(pickupDate);
  const end = new Date(returnDate);
  const diffMs = end.getTime() - start.getTime();
  const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  return Math.max(1, days);
}
